import { GcVariety } from 'interfaces/IGlobalCatalogue';
import type { IUnit } from 'interfaces/IInput';
import {
  IActiveConstituent,
  IGcProductGroup,
  InputStepType,
  PoisonSchedule,
  SeedDressRateMode,
} from 'interfaces/IInputStep';
import { InputTaskType } from 'interfaces/IInputTask';

import { STEP_SELECTOR_GROUP_DATA } from './stepOptions';

/** Row markers so useStepSelectorKeyboard keeps DOM focus and the arrow-key highlight on one row. */
export const PICKER_OPTION_ID_ATTRIBUTE = 'data-picker-option-id';
export const PICKER_VARIETY_ID_ATTRIBUTE = 'data-picker-variety-id';
/** Class on the flyout boxes portaled beside the step picker (App.css positions them). */
export const STEP_PICKER_FLYOUT_SELECTOR = '.step-picker-flyout';

/** Marks the Seed Treatment fields (flyout and edit two-panel) so Shift+Enter inside them confirms. */
export const SEED_TREATMENT_FIELDS_ATTRIBUTE = 'data-seed-treatment-fields';

/** Marks the Seed Treatment flyout's Add button, which a blocked Save sends focus to. */
export const FLYOUT_ADD_BUTTON_ATTRIBUTE = 'data-flyout-add';

/**
 * Row styling shared by StepInputSelector's plain list and the Seed / Seed Treatment / variety lists.
 * CatalogueOptionList keeps its own, since react-window rows need their own style and ARIA wiring.
 */
export const OPTION_ROW_BASE_CLASSNAME =
  'flex w-full items-start gap-2 border-b border-gray-bright px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-gray-smoke';

export interface SpecWithholdPeriod {
  periodType?: number | null;
  cropId?: string | null;
  snapCropName?: string | null;
  period?: number | null;
}

/**
 * One option in the step picker, from a live GC/MC catalogue (Plant Protection, Adjuvant,
 * Operation, Soil Input, Biostimulant) or the remaining static data (Production, Seed Dress).
 */
export interface StepSelectorOption {
  label: string;
  item: string;
  globalId: string | null;
  rate?: number | null;
  cost?: number | null;
  unit?: IUnit | null;
  useWater?: boolean | null;
  productGroups?: IGcProductGroup[] | null;
  activeConstituents?: IActiveConstituent[] | null;
  formulationCode?: string | null;
  formulation?: string | null;
  apvmaNumber?: string | null;
  poisonSchedule?: PoisonSchedule | null;
  withholdPeriodGrazing?: number | null;
  withholdPeriodCutting?: number | null;
  withholdPeriodHarvest?: number | null;
  withholdPeriodPlantback?: number | null;
  specWithholdPeriods?: SpecWithholdPeriod[] | null;
  reentry?: number | null;
  reentryUnit?: IUnit | null;
  reentryNotes?: string | null;
  nutrientN?: number | null;
  nutrientP?: number | null;
  nutrientK?: number | null;
  nutrientS?: number | null;
  nutrientCa?: number | null;
  nutrientMg?: number | null;
  nutrientNa?: number | null;
  nutrientB?: number | null;
  nutrientCu?: number | null;
  nutrientFe?: number | null;
  nutrientMn?: number | null;
  nutrientZn?: number | null;
  nutrientMo?: number | null;
  nutrientCl?: number | null;
  nutrientSi?: number | null;
  nutrientNi?: number | null;
  nutrientCo?: number | null;
  additive?: string | null;
  isSeedDressing?: boolean | null;
  co2eScope1?: number | null;
  co2eScope2?: number | null;
  co2eScope3?: number | null;
  co2eUnit?: IUnit | null;
  isLiquid?: boolean | null;
  rateUnitGroup?: string | null;
  bulkDensity?: number | null;
  bulkDensityUnit?: IUnit | null;
  targetGrade?: string | null;
  item2?: string | null;
  globalId2?: string | null;
  varieties?: GcVariety[] | null;
  actionModeId?: string | null;
  snapActionMode?: string | null;
  techStackId?: string | null;
  snapTechStack?: string | null;
  manufacturerId?: string | null;
  snapManufacturer?: string | null;
  supplierId?: string | null;
  snapSupplier?: string | null;
  linkedSeedStepId?: string | null;
  seedDressRateMode?: SeedDressRateMode | null;
  stepId?: string;
}

/** Seed Treatment fields being edited in the picker before they become a selection. */
export interface SeedDressDraft {
  rateMode: SeedDressRateMode;
  rate: number | null;
  unit: IUnit | null;
  cost: number | null;
  linkedStepId: string | null;
}

export interface StepSelectorGroup {
  key: string;
  label: string;
  stepType: InputStepType;
  options: StepSelectorOption[];
}

export const STEP_SELECTOR_GROUPS: StepSelectorGroup[] =
  STEP_SELECTOR_GROUP_DATA;

export const isStepAllowedForTask = (
  stepType: InputStepType,
  taskType: InputTaskType
): boolean => {
  switch (stepType) {
    case InputStepType.Generic:
    case InputStepType.Application:
      // Production tasks hold a single production step only
      return taskType !== InputTaskType.Production;
    case InputStepType.CropProtection:
    case InputStepType.Adjuvant:
    case InputStepType.SoilInput:
    case InputStepType.Biostimulant:
      return (
        taskType === InputTaskType.Application ||
        taskType === InputTaskType.CropEstablishment
      );
    case InputStepType.Production:
      return taskType === InputTaskType.Production;
    case InputStepType.Seed:
    case InputStepType.SeedDress:
      return taskType === InputTaskType.CropEstablishment;
    default:
      return false;
  }
};

export const getStepSelectorGroupsForTask = (
  taskType: InputTaskType
): StepSelectorGroup[] => {
  return STEP_SELECTOR_GROUPS.filter((group) =>
    isStepAllowedForTask(group.stepType, taskType)
  );
};

/** Matches on globalId, falling back to the item name for the rare option without one. */
export const stepOptionsMatch = (
  optionA: StepSelectorOption,
  optionB: StepSelectorOption
): boolean =>
  optionA.globalId && optionB.globalId
    ? optionA.globalId === optionB.globalId
    : optionA.item === optionB.item;

export const getStepOptionId = (option: StepSelectorOption): string =>
  option.globalId ?? option.item;

interface IdOrName {
  id?: string | null;
  name?: string | null;
}

/** Matches on ids when both sides have one, otherwise on names (case-insensitive). */
export const matchesByIdOrName = (
  candidate: IdOrName,
  target: IdOrName
): boolean =>
  candidate.id && target.id
    ? candidate.id === target.id
    : !!candidate.name &&
      candidate.name.toLowerCase() === target.name?.toLowerCase();

/** Varieties whose name contains the (trimmed, case-insensitive) search text. */
export const filterVarietiesByName = (
  varieties: GcVariety[],
  searchText: string
): GcVariety[] => {
  const normalized = searchText.trim().toLowerCase();
  if (!normalized) return varieties;
  return varieties.filter((variety) =>
    variety.varietyName.toLowerCase().includes(normalized)
  );
};

/** Reopen-the-secondary-panel tooltip for SeedVarietyTwoPanel ("varieties") and SeedTreatmentTwoPanel ("details"). */
export const getStepTwoPanelReopenTooltip = (
  primaryLabel: string,
  noun: string
): string => `Click to see ${primaryLabel}'s ${noun}`;

// Seed row/chip label once a variety has been chosen, e.g. "Barley (Jumbo)".
export const getSeedOptionDisplayLabel = (
  option: StepSelectorOption
): string =>
  option.item2 ? `${option.label} (${option.item2})` : option.label;

/**
 * Flattens crop + variety into one selection in IInputStepSeed's shape
 * (crop on item/globalId, variety on item2/globalId2), for mouse and keyboard picks alike.
 */
export const composeSeedVarietySelection = (
  crop: StepSelectorOption,
  variety: GcVariety
): StepSelectorOption => ({
  ...crop,
  item2: variety.varietyName,
  globalId2: variety.varietyId,
  techStackId: variety.techStackId ?? null,
  snapTechStack: variety.snapTechStack ?? null,
  manufacturerId: variety.manufacturerId ?? null,
  snapManufacturer: variety.snapManufacturer ?? null,
  supplierId: variety.supplierId ?? null,
  snapSupplier: variety.snapSupplier ?? null,
});

export interface OptionMetaTag {
  tag: 'M' | 'S';
  name: string;
}

export interface OptionMeta {
  labels: string[];
  tag: OptionMetaTag | null;
}

/** Maps a variety to an OptionMetaRow-compatible shape (manufacturer / supplier tags). */
export const varietyToMetaOption = (
  variety: GcVariety
): StepSelectorOption => ({
  label: variety.varietyName,
  item: variety.varietyName,
  globalId: variety.varietyId,
  snapManufacturer: variety.snapManufacturer,
  snapSupplier: variety.snapSupplier,
});

export const getOptionMeta = (option: StepSelectorOption): OptionMeta => {
  const hasRate = option.rate !== null && option.rate !== undefined;
  const hasCost = option.cost !== null && option.cost !== undefined;

  const labels: string[] = [];
  if (hasRate) {
    const rateUnit = option.unit?.unit;
    labels.push(`Rate: ${option.rate}${rateUnit ? `${rateUnit}` : ''}`);
  }
  if (hasCost) {
    labels.push(`Cost: $${option.cost}`);
  }

  const tag: OptionMetaTag | null = option.snapManufacturer
    ? { tag: 'M', name: option.snapManufacturer }
    : option.snapSupplier
      ? { tag: 'S', name: option.snapSupplier }
      : null;

  return { labels, tag };
};
